/* eslint-env browser */

import { get } from 'lodash';
import appConfig from 'Config';
import logger from 'Logger';
import { ClientSideError } from './errors';

const sanitize = (s = '') => {
  return s && s.trim().toLowerCase();
};

export const sendErrorToSentry = ({ error, errorInfo = {} }) => {
  if (typeof window !== 'undefined' && appConfig.get('clientError.sentry.enabled')) {
    window.Sentry?.withScope((scope) => {
      Object.keys(errorInfo).forEach((key) => {
        scope.setExtra(key, errorInfo[key]);
      });

      window.Sentry?.captureException(error);
    });
  }
};

export const shouldIgnore = (error) => {
  const errorMessagesToIgnore = [].concat(appConfig.get('clientError.ignore.descriptions'));
  const errorMessage = sanitize(error.message);
  const errorStack = sanitize(error.stack);

  return errorMessagesToIgnore.some((messageToIgnore) => {
    const msg = sanitize(messageToIgnore);
    const isIgnored = !!msg && (!!errorMessage.includes(msg) || !!errorStack.includes(msg));
    return isIgnored;
  });
};

export const processError = ({ error, errorInfo }) => {
  logger.error({ err: error, info: errorInfo });

  if (!shouldIgnore(error)) {
    sendErrorToSentry({ error, errorInfo });
  }

  return error;
};

// eslint-disable-next-line max-params
export const handleBrowserError = (message, file, line, col, error) => {
  return processError({
    error:
      error ||
      new ClientSideError({
        name: 'Unknown error name',
        message: message || 'Unknown error message',
        stack: 'Unknown stack',
      }),
    errorInfo: {
      message,
      file,
      line,
      col,
    },
  });
};

export const handlePromiseError = (domCustomEvent) => {
  return processError({
    error: get(
      domCustomEvent,
      'detail.reason',
      get(
        domCustomEvent,
        'reason',
        new ClientSideError({
          name: 'Unknown rejected promise name',
          message: 'Unknown rejected promise message',
          stack: ['Unknown stack'],
        }),
      ),
    ),
  });
};

export const handleComponentError = (error, errorInfo) => {
  return processError({
    error:
      error ||
      new ClientSideError({
        name: 'Unknown component error name',
        message: 'Unknown component error message',
        stack: ['Unknown stack'],
      }),
    errorInfo,
  });
};

export const start = () => {
  window.onerror = handleBrowserError;
  window.addEventListener('unhandledrejection', handlePromiseError);
  window.addEventListener('rejectionhandled', handlePromiseError);
};
